import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";

import {
  useWeightsGridAutosave,
  type WeightsGridColumn,
  type WeightsGridHandlers,
} from "./use-weights-grid-autosave";
import { WeightTotalCell } from "./weight-total-cell";
import { T } from "./weights-grid.i18n";

type WeightsGridProps = {
  title: string;
  description: string;
  /** Matrix scores of the risk model, one column each. */
  scores: number[];
  /** Products that can be weighted, one row each. */
  products: { id: string; name: string; isin: string | null }[];
  /** The portfolios of each risk score, one column each; a score without one has an empty column. */
  columns: Map<number, WeightsGridColumn[]>;
  /** Tooltip title of a risk score that has no portfolio yet. */
  emptyColumnTitle: (score: number) => string;
  /** Step of the weight inputs. */
  weightStep: string;
  /**
   * Whether a product can be added to a column (`null`: the risk score has no
   * portfolio yet). A cell that cannot is disabled unless it holds a weight.
   * Without it every cell is offered.
   */
  canAddCell?: (productId: string, columnId: string | null) => boolean;
  /** Tooltip of a cell `canAddCell` turns down. */
  notAddableHint?: string;
  handlers: WeightsGridHandlers;
};

/**
 * Product × portfolio matrix of weights (model or dynamic), the portfolios of
 * one risk score side by side. Each cell is saved on blur; a column is flagged
 * while its portfolio is incomplete.
 */
export function WeightsGrid({
  title,
  description,
  scores,
  products,
  columns,
  emptyColumnTitle,
  weightStep,
  canAddCell,
  notAddableHint,
  handlers,
}: WeightsGridProps) {
  const t = useT(T);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { cellValue, setValue, persist, isSaving } = useWeightsGridAutosave({
    columns,
    handlers,
    onError: setErrorMessage,
    messages: { weightOutOfRange: t.weightOutOfRange, failedToSave: t.failedToSave },
  });

  // One slot per portfolio; a risk score without one keeps a single empty slot.
  const slots = scores.flatMap((score) => {
    const scoreColumns: (WeightsGridColumn | undefined)[] = columns.get(score) ?? [];
    return (scoreColumns.length > 0 ? scoreColumns : [undefined]).map((column, index) => ({
      key: `${score}:${index}`,
      score,
      index,
      column,
      // Sets the portfolios of one risk score apart from the next score's.
      groupBorder: index === 0 ? "border-l" : "",
    }));
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          {scores.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t.noRiskScores}</p>
          ) : products.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t.noProducts}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left border-b min-w-[220px]">{t.productColumn}</th>
                    {slots.map(({ key, score, column, groupBorder }) => {
                      return (
                        <th
                          key={key}
                          className={`p-2 text-center border-b min-w-[110px] ${groupBorder} ${
                            column?.isIncomplete ? "bg-destructive/10 text-destructive" : ""
                          }`}
                        >
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <div className="cursor-default">
                                <div className="text-base font-bold">{score}</div>
                                {column?.inlineName && (
                                  <div className="text-xs font-normal text-muted-foreground truncate max-w-[100px] mx-auto">
                                    {column.inlineName}
                                  </div>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="font-medium">
                                {column?.tooltipTitle ?? emptyColumnTitle(score)}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {t.riskScore}: {score}
                              </div>
                              {column?.tooltipDescription && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {column.tooltipDescription}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td className="p-2 border-b">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{product.name}</span>
                          {product.isin && (
                            <code className="text-xs text-muted-foreground">{product.isin}</code>
                          )}
                        </div>
                      </td>
                      {slots.map(({ key, score, index, column, groupBorder }) => {
                        // A held weight stays editable so it can be changed or removed.
                        const notAddable =
                          !column?.cells[product.id] &&
                          canAddCell?.(product.id, column?.id ?? null) === false;
                        return (
                          <td
                            key={key}
                            title={notAddable ? notAddableHint : undefined}
                            className={`p-2 border-b text-center ${groupBorder} ${
                              column?.isIncomplete ? "bg-destructive/10" : ""
                            }`}
                          >
                            <Input
                              type="number"
                              step={weightStep}
                              min="0"
                              max="100"
                              className="w-24 h-8 text-sm text-center mx-auto"
                              placeholder=""
                              value={cellValue(product.id, score, index)}
                              disabled={notAddable || isSaving(product.id, score, index)}
                              onChange={(e) => setValue(product.id, score, index, e.target.value)}
                              onBlur={() => persist(product.id, score, index)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-t-2">
                    <td className="p-2 font-medium text-right">{t.totalRow}</td>
                    {slots.map(({ key, column, groupBorder }) => {
                      const incomplete = column?.isIncomplete ?? false;
                      return (
                        <td
                          key={key}
                          className={`p-2 text-center ${groupBorder} ${incomplete ? "bg-destructive/10" : ""}`}
                        >
                          <WeightTotalCell
                            total={column?.total ?? 0}
                            isComplete={incomplete ? false : column?.hasProducts ? true : null}
                            stacked
                          />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertMessage
        open={errorMessage !== null}
        onOpenChange={(open) => {
          if (!open) setErrorMessage(null);
        }}
        title={t.error}
        description={errorMessage ?? ""}
        variant="destructive"
      />
    </>
  );
}
